/**
 * Candle transformations — ports of the JSR_DailyBlast pinescript formulas.
 * Given raw OHLC rows, produce alternate candle representations:
 *   - HA  : Smoothed Heikin-Ashi        (ha_close_s = ema((o+h+l+c)/4, 3))
 *   - CSH : Custom Smooth HA             (ema(ha, 10))
 *   - CSL : Custom Smooth Linreg         (HA of linreg-smoothed OHLC)
 *   - CLH : Custom Linreg HA             (avg of CSH and CSL)
 *   - CLN : Candle + Linear regressioN  (PASSTHROUGH here — see clnCandles.ts)
 * Output preserves {time, open, high, low, close, volume} shape so the chart
 * can render them with existing color/rendering logic unchanged.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { isCalendarInterval, nextBucketStart } from './intervalCatalog';

export type CandleType = 'Regular' | 'HA' | 'CSH' | 'CSL' | 'CLH' | 'CLN';

export const CANDLE_TYPES: { value: CandleType; label: string; title: string }[] = [
  { value: 'Regular', label: 'Regular', title: 'Standard OHLC candles' },
  { value: 'HA', label: 'HA', title: 'Heikin-Ashi (smoothed, EMA 3)' },
  { value: 'CSH', label: 'CSH', title: 'Custom Smooth HA (EMA 10 of HA)' },
  { value: 'CSL', label: 'CSL', title: 'Custom Smooth Linreg (linreg 14 + HA + EMA 10)' },
  { value: 'CLH', label: 'CLH', title: 'Custom Linreg HA (average of CSH & CSL)' },
  { value: 'CLN', label: 'CLN', title: 'Candle + Linear regressioN — real OHLC candles with a wickless linear-regression body drawn on top. BOTH layers take their colour from the LR direction, so a red bar inside a rising regression prints bullish. The Wick toggle controls the real candle only; regression length is set in Chart Settings.' },
];

const LR_LEN = 14;

/** Exponential moving average. Returns array of length src.length.
 *  Warm-up (< period bars): CUMULATIVE MEAN of the bars so far — NOT the raw
 *  price. The old raw-passthrough warm-up snapped to the SMA seed at bar
 *  period-1, a built-in discontinuity that made CLH/CSH/CSL candles "jump"
 *  whenever a chain re-seeded (window change / synthetic-series hole). The
 *  cumulative mean converges smoothly into the same SMA seed. */
export function ema(src: number[], period: number): number[] {
  const out: number[] = new Array(src.length);
  const k = 2 / (period + 1);
  let prev = 0;
  let runSum = 0;
  for (let i = 0; i < src.length; i++) {
    if (i < period - 1) {
      runSum += src[i];
      prev = runSum / (i + 1);
    } else if (i === period - 1) {
      runSum += src[i];
      prev = runSum / period; // same SMA seed as before
    } else {
      prev = src[i] * k + prev * (1 - k);
    }
    out[i] = prev;
  }
  return out;
}

/** Linear regression value at the current bar (offset=0).
 *  Fits y = a + b*x over the last `period` bars (x = 0..period-1) and returns
 *  the fitted value at x = period-1. Matches pine's `ta.linreg(src, N, 0)`. */
export function linreg(src: number[], period: number): number[] {
  const out: number[] = new Array(src.length);
  // Warm-up bars fit over WHATEVER is available (n = i+1) instead of passing the
  // raw price through — same smooth-warm-up rationale as ema() above.
  for (let i = 0; i < src.length; i++) {
    const n = Math.min(period, i + 1);
    if (n === 1) { out[i] = src[i]; continue; }
    const sumX = (n * (n - 1)) / 2;
    const sumXX = (n * (n - 1) * (2 * n - 1)) / 6;
    const denom = n * sumXX - sumX * sumX;
    let sumY = 0, sumXY = 0;
    for (let x = 0; x < n; x++) {
      const y = src[i - (n - 1) + x];
      sumY += y;
      sumXY += x * y;
    }
    const b = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    const a = (sumY - b * sumX) / n;
    out[i] = a + b * (n - 1);
  }
  return out;
}

function transformHA(candles: CandleRow[]): CandleRow[] {
  if (!candles.length) return candles;
  const mid = candles.map(c => (c.open + c.high + c.low + c.close) / 4);
  const haClose = ema(mid, 3);
  const haOpen: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i === 0) haOpen.push((candles[0].open + candles[0].close) / 2);
    else haOpen.push((haOpen[i - 1] + haClose[i - 1]) / 2);
  }
  return candles.map((c, i) => ({
    time: c.time,
    open: haOpen[i],
    close: haClose[i],
    high: Math.max(c.high, haOpen[i], haClose[i]),
    low: Math.min(c.low, haOpen[i], haClose[i]),
    volume: c.volume,
  }));
}

function transformCSH(candles: CandleRow[]): CandleRow[] {
  if (!candles.length) return candles;
  // Compute base HA first
  const mid = candles.map(c => (c.open + c.high + c.low + c.close) / 4);
  const haClose = ema(mid, 3);
  const haOpen: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i === 0) haOpen.push((candles[0].open + candles[0].close) / 2);
    else haOpen.push((haOpen[i - 1] + haClose[i - 1]) / 2);
  }
  // Smooth with EMA 10
  const shaO = ema(haOpen, 10);
  const shaC = ema(haClose, 10);
  return candles.map((c, i) => ({
    time: c.time,
    open: shaO[i],
    close: shaC[i],
    high: Math.max(c.high, shaO[i], shaC[i]),
    low: Math.min(c.low, shaO[i], shaC[i]),
    volume: c.volume,
  }));
}

/** Returns { slrO2, slrC2 } — the linreg-smoothed HA bodies (CSL). */
function computeSLR(candles: CandleRow[]): { o: number[]; c: number[] } {
  const opens = candles.map(c => c.open);
  const closes = candles.map(c => c.close);
  const highs = candles.map(c => c.high);
  const lows = candles.map(c => c.low);
  const lrO = linreg(opens, LR_LEN);
  const lrC = linreg(closes, LR_LEN);
  const slrO = ema(lrO, 10);
  const slrC = ema(lrC, 10);
  const slrH = ema(highs, 10);
  const slrL = ema(lows, 10);
  const slrHaClose: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    slrHaClose.push((slrO[i] + slrH[i] + slrL[i] + slrC[i]) / 4);
  }
  const slrHaOpen: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i === 0) slrHaOpen.push((slrO[0] + slrC[0]) / 2);
    else slrHaOpen.push((slrHaOpen[i - 1] + slrHaClose[i - 1]) / 2);
  }
  return { o: ema(slrHaOpen, 10), c: ema(slrHaClose, 10) };
}

function transformCSL(candles: CandleRow[]): CandleRow[] {
  if (!candles.length) return candles;
  const slr = computeSLR(candles);
  return candles.map((c, i) => ({
    time: c.time,
    open: slr.o[i],
    close: slr.c[i],
    high: Math.max(c.high, slr.o[i], slr.c[i]),
    low: Math.min(c.low, slr.o[i], slr.c[i]),
    volume: c.volume,
  }));
}

function transformCLH(candles: CandleRow[]): CandleRow[] {
  if (!candles.length) return candles;
  // Need both CSH (sha_o2, sha_c2) and CSL (slr_o2, slr_c2)
  const mid = candles.map(c => (c.open + c.high + c.low + c.close) / 4);
  const haClose = ema(mid, 3);
  const haOpen: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i === 0) haOpen.push((candles[0].open + candles[0].close) / 2);
    else haOpen.push((haOpen[i - 1] + haClose[i - 1]) / 2);
  }
  const shaO = ema(haOpen, 10);
  const shaC = ema(haClose, 10);
  const slr = computeSLR(candles);
  return candles.map((c, i) => {
    const o = (shaO[i] + slr.o[i]) / 2;
    const cl = (shaC[i] + slr.c[i]) / 2;
    return {
      time: c.time,
      open: o,
      close: cl,
      high: Math.max(c.high, o, cl),
      low: Math.min(c.low, o, cl),
      volume: c.volume,
    };
  });
}

export function transformCandles(candles: CandleRow[], type: CandleType): CandleRow[] {
  switch (type) {
    case 'Regular': return candles;
    case 'HA':  return transformHA(candles);
    case 'CSH': return transformCSH(candles);
    case 'CSL': return transformCSL(candles);
    case 'CLH': return transformCLH(candles);
    // CLN is a COMPOSITE type: the series keeps the real OHLC (so the crosshair,
    // OHLC legend, price scale, LTP line and P&L badge all read true prices) and
    // the regression bodies are painted over it by LrBodyPrimitive. There is
    // deliberately no geometry change here — see clnCandles.ts.
    case 'CLN': return candles;
    default:    return candles;
  }
}

/**
 * Drop the still-forming HTF bar so a consumer sees only CLOSED higher-timeframe
 * candles and cannot repaint. Times are IST-shifted seconds (the chart's axis),
 * which is why `now` is shifted by the same 19800 before the comparison.
 *
 * Lives here rather than in ChartsPage because BOTH the page (strategy HTF
 * zones) and TradingChart (the CLN HTF context band) need it, and TradingChart
 * cannot import from the page that renders it.
 */
export function dropFormingHtfBars(htf: CandleRow[], htfMin: number): CandleRow[] {
  if (!htf?.length || !htfMin) return htf;
  const nowIst = Math.floor(Date.now() / 1000) + 19800;
  const lastOpen = htf[htf.length - 1].time as number;
  // A calendar month is 28-31 days, so `lastOpen + htfMin * 60` is off by up to
  // a day in either direction for 1M — it would keep a still-forming January on
  // screen and drop a finished February. Ask the catalog when the bar really
  // closes. Fixed-size intervals keep the original arithmetic: their bars are
  // session-anchored and this function is not told which exchange it is looking
  // at, so it must not assume a bucket grid it cannot see.
  const closesAt = isCalendarInterval(htfMin) ? nextBucketStart(lastOpen, htfMin) : lastOpen + htfMin * 60;
  return closesAt > nowIst ? htf.slice(0, -1) : htf;
}
